import React from 'react'
import { APP_CONSTANTS } from 'src/consumer/constants'
import logo_brand from 'src/assets/logo_brand.png'

/**
 * PublicLiveSessionPageSkeleton
 * 
 * A lightweight, visually appealing skeleton loader specifically designed for the
 * Public Live Session Page (/v/:sessionSlug). This component:
 * 
 * 1. Provides immediate visual feedback while heavy Agora SDK loads
 * 2. Matches the final UI layout to minimize layout shift
 * 3. Uses pure CSS animations (no JS) for smooth performance
 * 4. Shows a reassuring progress indicator for users on slow networks
 * 5. Is intentionally lightweight - no MUI or heavy dependencies
 * 
 * This skeleton is critical for perceived performance as the actual page
 * loads heavy video conferencing dependencies (~800KB+).
 */
export default function PublicLiveSessionPageSkeleton() {
  return (
    <div style={styles.container}>
      {/* Inline keyframes for animation - no external CSS needed */}
      <style>{keyframes}</style>
      
      {/* Main content area - matches final layout */}
      <div style={styles.contentWrapper}>
        {/* Header Section - Logo and Session Info Skeleton */}
        <div style={styles.headerCard}>
          <div style={styles.headerContent}>
            {/* Logo - actual image for brand consistency */}
            <img
              src={APP_CONSTANTS.APP_LOGO_LINK}
              alt=""
              style={styles.logo}
              onError={(e) => {
                e.target.src = logo_brand
              }}
            />
            
            {/* Session Info Skeleton */}
            <div style={styles.sessionInfo}>
              <div style={{ ...styles.skeletonBox, ...styles.titleSkeleton }} />
              <div style={styles.metaGroup}>
                <div style={{ ...styles.skeletonBox, ...styles.metaSkeleton }} />
                <div style={{ ...styles.skeletonBox, ...styles.metaSkeleton, width: '140px' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Join Section */}
        <div style={styles.joinSection}>
          {/* Privacy/Terms Box Skeleton */}
          <div style={styles.termsCard}>
            <div style={{ ...styles.skeletonBox, ...styles.termsSkeleton }} />
            <div style={{ ...styles.skeletonBox, ...styles.termsSkeleton, width: '80%' }} />
          </div>

          {/* Join Button Skeleton with shimmer effect */}
          <div style={styles.joinButtonSkeleton}>
            <div style={styles.shimmerOverlay} />
            <span style={styles.buttonText}>Preparing session...</span>
          </div>
        </div>

        {/* Progress Indicator - Reassures users something is happening */}
        <div style={styles.progressSection}>
          <div style={styles.progressBar}>
            <div style={styles.progressFill} />
          </div>
          <p style={styles.progressText}>
            Loading video conferencing...
          </p>
        </div>

        {/* Connection Status Dots - Visual feedback for slow networks */}
        <div style={styles.statusDots}>
          <span style={{ ...styles.dot, animationDelay: '0ms' }} />
          <span style={{ ...styles.dot, animationDelay: '200ms' }} />
          <span style={{ ...styles.dot, animationDelay: '400ms' }} />
        </div>
      </div>
    </div>
  )
}

// CSS-in-JS styles - no external dependencies
const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #fafbfc 0%, #f5f7fa 50%, #f0f2f5 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '20px',
    paddingTop: '32px',
    fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  
  contentWrapper: {
    width: '100%',
    maxWidth: '600px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  
  headerCard: {
    background: '#ffffff',
    borderRadius: '20px',
    padding: '24px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)',
  },
  
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  
  logo: {
    height: '60px',
    width: '60px',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    objectFit: 'cover',
  },
  
  sessionInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  
  metaGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  
  skeletonBox: {
    background: 'linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s ease-in-out infinite',
    borderRadius: '8px',
  },
  
  titleSkeleton: {
    width: '70%',
    height: '24px',
    marginBottom: '4px',
  },
  
  metaSkeleton: {
    width: '120px',
    height: '16px',
  },
  
  joinSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    alignItems: 'center',
  },
  
  termsCard: {
    width: '100%',
    background: '#ffffff',
    borderRadius: '16px',
    padding: '18px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  
  termsSkeleton: {
    width: '100%',
    height: '14px',
  },
  
  joinButtonSkeleton: {
    width: '100%',
    maxWidth: '360px',
    height: '56px',
    borderRadius: '16px',
    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(99, 102, 241, 0.3) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    border: '1px solid rgba(139, 92, 246, 0.2)',
  },
  
  shimmerOverlay: {
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
    animation: 'buttonShimmer 2s ease-in-out infinite',
  },
  
  buttonText: {
    color: 'rgba(139, 92, 246, 0.8)',
    fontSize: '16px',
    fontWeight: 600,
    zIndex: 1,
  },
  
  progressSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    marginTop: '8px',
  },
  
  progressBar: {
    width: '200px',
    height: '4px',
    background: '#e5e7eb',
    borderRadius: '2px',
    overflow: 'hidden',
  },
  
  progressFill: {
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, #8b5cf6, #6366f1)',
    borderRadius: '2px',
    animation: 'progress 2s ease-in-out infinite',
  },
  
  progressText: {
    fontSize: '14px',
    color: '#6b7280',
    fontWeight: 500,
    margin: 0,
  },
  
  statusDots: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
    marginTop: '16px',
  },
  
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#8b5cf6',
    animation: 'dotPulse 1.4s ease-in-out infinite',
  },
}

// CSS Keyframes as a string (injected via style tag)
const keyframes = `
  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  
  @keyframes buttonShimmer {
    0% { left: -100%; }
    50%, 100% { left: 100%; }
  }
  
  @keyframes progress {
    0% { transform: translateX(-100%); }
    50% { transform: translateX(0%); }
    100% { transform: translateX(100%); }
  }
  
  @keyframes dotPulse {
    0%, 80%, 100% { 
      transform: scale(0.6);
      opacity: 0.5;
    }
    40% { 
      transform: scale(1);
      opacity: 1;
    }
  }
  
  @media (prefers-reduced-motion: reduce) {
    * {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
    }
  }
`

